import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './SalesMyRequests.module.css';
import {
  getSalesUserRequests,
  updateSalesRequestOrderDate,
} from '../api/salesRequestApi';
import AppLayout from '../components/AppLayout';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

const translations = {
  en: {
    pageTitle: 'My Sales Requests',
    explanationText:
      'Review every request you have submitted and track the latest manager updates in one organized view.',
    failedFetch: 'Failed to fetch your requests. Please try again.',
    noRequests: 'You have not submitted any requests yet.',
    requestId: 'Request ID',
    itemsHeader: 'Items in this request',
    product: 'Product',
    sort: 'Sort',
    color: 'Color',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    submissionDate: 'Submission Date',
    orderDate: 'Order date',
    saveDate: 'Save date',
    dateUpdated: 'Order date updated.',
    dateUpdateFailed: 'Failed to update order date.',
    action: 'Action',
    status: 'Status',
    paymentType: 'Payment',
    cash: 'Cash',
    postpaid: 'Postpaid',
    paymentDueDate: 'Due date',
    subtotal: 'Subtotal',
    requestedDiscount: 'Requested discount',
    discountStatus: 'Discount status',
    discountAmount: 'Discount amount',
    totalAfterDiscount: 'Total after discount',
    managerNotes: 'Manager Notes',
    noNotes: 'No manager notes yet.',
    customer: 'Customer',
    customerId: 'Customer ID',
    loading: 'Loading...',
    close: 'Close',
    english: 'English',
    arabic: 'عربي',
    homepage: 'Homepage',
    statusPending: 'Pending',
    statusDateChanged: 'Date Changed',
    statusAccepted: 'Accepted',
    statusRejected: 'Rejected',
    statusHeld: 'Held',
    statusCancelled: 'Cancelled',
    itemsCountLabel: 'Items',
  },
  ar: {
    pageTitle: 'طلباتي في المبيعات',
    explanationText:
      'راجع كل طلب قدمته وتابع أحدث تحديثات المدير ضمن عرض منظم وحديث.',
    failedFetch: 'فشل في جلب طلباتك. حاول مرة أخرى.',
    noRequests: 'لم تقم بتقديم أي طلبات حتى الآن.',
    requestId: 'معرف الطلب',
    itemsHeader: 'العناصر في هذا الطلب',
    product: 'المنتج',
    sort: 'الصنف',
    color: 'اللون',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'إجمالي السطر',
    submissionDate: 'تاريخ التقديم',
    orderDate: 'تاريخ الأوردر',
    saveDate: 'حفظ التاريخ',
    dateUpdated: 'تم تحديث تاريخ الأوردر.',
    dateUpdateFailed: 'فشل تحديث تاريخ الأوردر.',
    action: 'الإجراء',
    status: 'الحالة',
    paymentType: 'الدفع',
    cash: 'كاش',
    postpaid: 'آجل',
    paymentDueDate: 'تاريخ الاستحقاق',
    subtotal: 'الإجمالي قبل الخصم',
    requestedDiscount: 'الخصم المطلوب',
    discountStatus: 'حالة الخصم',
    discountAmount: 'قيمة الخصم',
    totalAfterDiscount: 'الإجمالي بعد الخصم',
    managerNotes: 'ملاحظات المدير',
    noNotes: 'لا توجد ملاحظات من المدير بعد.',
    customer: 'العميل',
    customerId: 'رقم العميل',
    loading: 'جارٍ التحميل...',
    close: 'إغلاق',
    english: 'الإنجليزية',
    arabic: 'عربي',
    homepage: 'الصفحة الرئيسية',
    statusPending: 'قيد الانتظار',
    statusDateChanged: 'تم تغيير التاريخ',
    statusAccepted: 'مقبول',
    statusRejected: 'مرفوض',
    statusHeld: 'محتجز',
    statusCancelled: 'ملغي',
    itemsCountLabel: 'العناصر',
  },
};

const statusClassMap = {
  pending: 'statusChipPending',
  'date-changed': 'statusChipDateChanged',
  accepted: 'statusChipAccepted',
  rejected: 'statusChipRejected',
  held: 'statusChipHeld',
  cancelled: 'statusChipCancelled',
};

const statusLabelKeyMap = {
  pending: 'statusPending',
  'date-changed': 'statusDateChanged',
  accepted: 'statusAccepted',
  rejected: 'statusRejected',
  held: 'statusHeld',
  cancelled: 'statusCancelled',
};

const SalesMyRequests = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key] || key;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [dateDrafts, setDateDrafts] = useState({});
  const [dateSubmitting, setDateSubmitting] = useState(null);

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  const toggleLanguage = () => {
    setLanguage((previous) => (previous === 'en' ? 'ar' : 'en'));
  };

  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  const formatDate = (value) => {
    return formatDateDDMMYYYY(value, '—');
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return parsedDate.toISOString().split('T')[0];
  };

  const getRequestOrderDate = (request) => {
    const firstItem = Array.isArray(request?.items) ? request.items[0] : null;
    return firstItem?.itemSubmissionDate || request?.createdAt;
  };

  const getStatusLabel = (status) => {
    const key = statusLabelKeyMap[status];
    if (!key) {
      return status;
    }
    return t(key);
  };

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const response = await getSalesUserRequests();
        const requestRows = Array.isArray(response.data) ? response.data : [];
        setRequests(requestRows);
        setDateDrafts(
          requestRows.reduce((drafts, request) => {
            drafts[request._id] = toDateInputValue(getRequestOrderDate(request));
            return drafts;
          }, {})
        );
      } catch (error) {
        console.error('Error fetching sales user requests:', error);
        setModalMessage(t('failedFetch'));
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
  };

  const handleDateDraftChange = (requestId, value) => {
    setDateDrafts((prev) => ({
      ...prev,
      [requestId]: value,
    }));
  };

  const handleSaveOrderDate = async (requestId) => {
    const orderDate = dateDrafts[requestId];
    if (!orderDate) {
      setModalMessage(t('dateUpdateFailed'));
      setShowModal(true);
      return;
    }

    setDateSubmitting(requestId);
    try {
      const response = await updateSalesRequestOrderDate(requestId, orderDate);
      setRequests((prev) =>
        prev.map((request) =>
          request._id === requestId ? response.data : request
        )
      );
      setDateDrafts((prev) => ({
        ...prev,
        [requestId]: toDateInputValue(getRequestOrderDate(response.data)),
      }));
      setModalMessage(t('dateUpdated'));
      setShowModal(true);
    } catch (error) {
      console.error('Error updating order date:', error);
      setModalMessage(error?.response?.data?.message || t('dateUpdateFailed'));
      setShowModal(true);
    } finally {
      setDateSubmitting(null);
    }
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      subtitle={t('explanationText')}
      language={language}
      languageLabel={language === 'en' ? t('arabic') : t('english')}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.page}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>{t('loading')}</p>
        </div>
      )}

      <section className={styles.content}>
        {!loading && requests.length === 0 && (
          <div className={styles.emptyState}>{t('noRequests')}</div>
        )}

        {requests.length > 0 && (
          <div className={styles.cardsGrid}>
            {requests.map((request) => {
              const customerName =
                request.customer?.fullName ||
                request.customerSnapshot?.fullName ||
                'N/A';
              const customerCode =
                request.customer?.customerID ||
                request.customerSnapshot?.customerID ||
                'N/A';
              const items = Array.isArray(request.items) ? request.items : [];
              const summary = request.orderSummary || {};
              const paymentLabel =
                request.paymentType === 'postpaid' ? t('postpaid') : t('cash');

              return (
                <article key={request._id} className={styles.card}>
                  <header className={styles.cardHeader}>
                    <div className={styles.cardHeaderTitle}>
                      <span className={styles.requestId}>
                        {t('requestId')}: {request._id}
                      </span>
                      <span
                        className={`${styles.statusChip} ${
                          styles[statusClassMap[request.status]] || ''
                        }`}
                      >
                        {t('status')}: {getStatusLabel(request.status)}
                      </span>
                    </div>
                    <div className={styles.cardMeta}>
                      <span>
                        {t('submissionDate')}: {formatDate(request.createdAt)}
                      </span>
                      <span>
                        {t('action')}: {request.action}
                      </span>
                    </div>
                  </header>

                  <div className={styles.customerSummary}>
                    <span>
                      {t('customer')}: {customerName}
                    </span>
                    <span>
                      {t('customerId')}: {customerCode}
                    </span>
                    <span>
                      {t('paymentType')}: {paymentLabel}
                    </span>
                    {request.paymentType === 'postpaid' && (
                      <span>
                        {t('paymentDueDate')}: {formatDate(request.paymentDueDate)}
                      </span>
                    )}
                  </div>

                  <div className={styles.orderDateEditor}>
                    <label>
                      <span>{t('orderDate')}</span>
                      <input
                        type="date"
                        value={dateDrafts[request._id] || ''}
                        onChange={(event) =>
                          handleDateDraftChange(request._id, event.target.value)
                        }
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => handleSaveOrderDate(request._id)}
                      disabled={dateSubmitting === request._id}
                    >
                      {dateSubmitting === request._id ? '...' : t('saveDate')}
                    </button>
                  </div>

                  <div className={styles.financialSummary}>
                    <div>
                      <span>{t('subtotal')}</span>
                      <strong>{formatCurrency(summary.subtotal)}</strong>
                    </div>
                    <div>
                      <span>{t('requestedDiscount')}</span>
                      <strong>{Number(request.requestedDiscountPercent || 0)}%</strong>
                    </div>
                    <div>
                      <span>{t('discountStatus')}</span>
                      <strong>{request.discountApprovalStatus || 'not-requested'}</strong>
                    </div>
                    <div>
                      <span>{t('discountAmount')}</span>
                      <strong>{formatCurrency(summary.discountAmount)}</strong>
                    </div>
                    <div>
                      <span>{t('totalAfterDiscount')}</span>
                      <strong>{formatCurrency(summary.totalAfterDiscount)}</strong>
                    </div>
                  </div>

                  <div className={styles.managerNotes}>
                    <span className={styles.metaLabel}>{t('managerNotes')}</span>
                    <p className={styles.metaValue}>
                      {request.managerResponse?.notes
                        ? request.managerResponse.notes
                        : t('noNotes')}
                    </p>
                  </div>

                  <div className={styles.itemsSection}>
                    <div className={styles.itemsHeaderRow}>
                      <h3 className={styles.itemsTitle}>{t('itemsHeader')}</h3>
                      <span
                        className={styles.itemsCount}
                        aria-label={`${items.length} ${t('itemsCountLabel')}`}
                      >
                        {items.length}
                      </span>
                    </div>
                    <div className={styles.itemsList}>
                      {items.map((item, index) => (
                        <div key={`${request._id}-item-${index}`} className={styles.itemCard}>
                          <div className={styles.itemTitleRow}>
                            <span className={styles.itemName}>{item.product}</span>
                            <span className={styles.itemQuantity}>
                              {t('quantity')}: {item.quantity}
                            </span>
                            <span className={styles.itemQuantity}>
                              {t('lineTotal')}: {formatCurrency(item.lineTotal)}
                            </span>
                          </div>
                          <div className={styles.itemDetails}>
                            <span>
                              {t('sort')}: {item.sortName || '—'}
                            </span>
                            <span>
                              {t('color')}: {item.color || '—'}
                            </span>
                            <span>
                              {t('unitPrice')}: {formatCurrency(item.unitPrice)}
                            </span>
                            <span>
                              {t('submissionDate')}: {formatDate(item.itemSubmissionDate)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <footer className={styles.cardFooter}>
                    <span>
                      {t('action')}: <strong>{request.action}</strong>
                    </span>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {showModal && (
        <div className={styles.modalBackdrop} role="alertdialog" aria-modal="true">
          <div className={styles.modalContent}>
            <p>{modalMessage}</p>
            <button type="button" className={styles.closeButton} onClick={closeModal}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default SalesMyRequests;
